import useAppStore from "@/context/store";
import useListenPinsPopstate from "@/utils/hooks/useListenPinsPopstate";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EVENT } from "../../constants/event";
import FoldCard from "../FoldCard";
import HBList from "../HBList";
import PinCard from "../PinCard";
import { usePinActionsWithCard } from "../PinCard/actions";
import QuestionaireCard from "../QuestionnaireCard";
import RecommendCard from "../RecommendCard";
const PinActions = dynamic(import("../PinCard/actions"), { ssr: false });

/**
 * @param {{
 * hideUser?: boolean;
 * dataSource?: Array<any>;
 * showBoardLink?: boolean;
 * disableModal?: boolean;
 * disableQueue?: any;
 * isModal?: boolean;
 * hasMore?: any;
 * onLoadMore?: any;
 * disableFile?: any;
 * showDownload?: boolean;
 * isSearch?: boolean;
 * }}
 */
export default function PinList({
  hideUser,
  dataSource,
  showBoardLink,
  showMaterialTag = true,
  disableModal,
  disableQueue,
  isModal,
  disableFile, // 是否点击详情的时候把文件带过去
  showDownload,
  isSearch,
  ...props
}) {
  const { column: columnNum, currentPin } = useAppStore(
    ({ column, currentPin }) => ({
      column,
      currentPin,
    })
  );
  const [list, setList] = useState(dataSource);
  const deleteMapRef = useRef({});
  useEffect(() => {
    setList(dataSource);
  }, [dataSource]);

  const filter = useCallback((dataSource) => {
    return dataSource.filter((record) => {
      if (deleteMapRef.current[record.pin_id]) {
        return false;
      }
      if (record.file?.file_id) {
        return record.file_id === record.file.file_id;
      }
      return true;
    });
  }, []);

  useEffect(() => {
    const onDelete = (evt) => {
      const pinIds = Array.isArray(evt.detail) ? evt.detail : [evt.detail];
      pinIds.forEach((pinId) => {
        deleteMapRef.current[pinId] = true;
      });

      setList((list) => {
        return list.filter((pin) => !pinIds.includes(pin.pin_id));
      });
    };
    window.addEventListener(EVENT.DELETE_PIN, onDelete);
    return () => {
      window.removeEventListener(EVENT.DELETE_PIN, onDelete);
    };
  }, []);

  const { pinActionsProps, onMouseEnterCard, onMouseLeaveCard } =
    usePinActionsWithCard();

  const searchInfo = useMemo(() => {
    if (!isSearch) {
      return null;
    }
    const distCodes = [];
    const fileIds = [];
    let count = 0;
    dataSource?.forEach((item) => {
      if (item && item?.ext?.dist_code && item?.file_id && count < 120) {
        count++;
        distCodes.push(item?.ext?.dist_code);
        fileIds.push(item?.file_id);
      }
    }, []);

    return { distCodes, fileIds };
  }, [isSearch, dataSource]);

  const render = useCallback(
    (record) => {
      // 广告
      if (record.type === "recommend") {
        return <RecommendCard key={record.pin_id} record={record} />;
      }
      // 问卷
      if (record.type === "questionaire") {
        return (
          <QuestionaireCard
            key={record.pin_id}
            record={record}
            {...searchInfo}
          />
        );
      }
      // 折叠
      if (record.folding_num && record.folding_num > 0) {
        return <FoldCard key={record.pin_id} record={record} />;
      }
      return (
        <PinCard
          key={record.pin_id}
          record={record}
          isModal={isModal}
          showBoardLink={showBoardLink}
          disableModal={disableModal}
          disableFile={disableFile}
          hideUser={hideUser}
          showMaterialTag={showMaterialTag}
          showDownload={showDownload}
          onMouseEnterCard={onMouseEnterCard}
          onMouseLeaveCard={onMouseLeaveCard}
        />
      );
    },
    [
      searchInfo,
      showBoardLink,
      hideUser,
      disableModal,
      disableFile,
      showDownload,
      isModal,
      showMaterialTag,
      onMouseEnterCard,
      onMouseLeaveCard,
    ]
  );

  useEffect(() => {
    if (!disableQueue) {
      if (list.length > 0) {
        const map = {};
        useAppStore.setState({
          pinQueue: list.filter((item) => {
            if (map[item.pin_id] || item.type === "recommend") {
              return false;
            }
            map[item.pin_id] = true;
            if (deleteMapRef.current[item.pin_id]) {
              return false;
            }
            if (item.file?.file_id) {
              return item.file_id === item.file.file_id;
            }
            return true;
          }),
        });
      }
      return () => {
        useAppStore.setState({ pinQueue: [] });
      };
    }
  }, [list, disableQueue, currentPin]);
  // 每次点击pincard时都重新设置 pinQueue,。解决折叠弹窗查看pin详情，退出后会清空 pinQueue，导致feed流pin详情无法切换

  useListenPinsPopstate();
  return (
    <>
      <HBList
        {...props}
        loginGuide={true}
        uniqueKey="pin_id"
        dataSource={list}
        filter={filter}
        masonryOptions={{
          columnNum,
          brickId: "pin_id",
          masonryKey: props.masonryKey,
        }}
        render={render}
      />
      <PinActions {...pinActionsProps} showDownload={showDownload} />
    </>
  );
}
